import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { MetaBox } from '@/components/admin/MetaBox';
import { EmptyState, ErrorState, ForbiddenState, SkeletonRows } from '@/components/admin/states';
import {
  ATTENDANCE_STATUS_LABELS,
  type AttendanceStatus,
} from '@/features/events/attendanceApi';
import { MEMBER_STATUS_LABELS, type MemberStatus } from '@/features/members/api';
import { PhotoPreview } from '@/features/members/MemberPhotoField';
import { MemberRelatedNav } from '@/features/members/MemberRelatedNav';
import {
  fetchMemberPortfolio,
  fetchOwnPortfolio,
  type Portfolio,
  type SurveyEntry,
} from '@/features/portfolio/api';
import { ApiError } from '@/lib/api/http';

/** 年度の選択肢（現在の年度から3年ぶん遡る） */
function fiscalYearOptions(): number[] {
  const now = new Date();
  const current = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
  return [current, current - 1, current - 2];
}

const SURVEY_STATUS_LABELS: Record<string, string> = {
  not_started: '未提出',
  draft: '下書き',
  submitted: '提出済み',
};

/**
 * 年間ポートフォリオ（D-113）。
 *
 * 先方の「会員マイページ」構成に対応する集約ビュー。
 * 1年分（4月〜翌3月）のイベント参加・課題の回答・採点・社長アンケートを1枚に並べる。
 * 新しいデータは持たず、既にあるものを読み直して表示するだけ。
 *
 * `/portfolio`      … 受講生が自分のものを見る
 * `/members/:id/portfolio` … 事務局・理事・講師・担当サクセスが会員を選んで見る
 */
export function PortfolioPage(): ReactElement {
  const { memberId } = useParams();
  const { user } = useAuth();
  const [year, setYear] = useState<number>(fiscalYearOptions()[0] as number);

  const query = useQuery({
    queryKey: ['portfolio', memberId ?? 'me', year],
    queryFn: () =>
      memberId ? fetchMemberPortfolio(memberId, year) : fetchOwnPortfolio(year),
  });

  if (query.isPending) return <SkeletonRows rows={6} />;
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 403) return <ForbiddenState />;
    return <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />;
  }

  const data = query.data;
  const isSelf = !memberId || user?.member?.id === data.member.id;

  return (
    <>
      <PageHeader
        title={isSelf ? '年間ポートフォリオ' : `${data.member.name} さんの年間ポートフォリオ`}
        description={`${data.fiscal_year}年度（${data.fiscal_year}年4月〜${data.fiscal_year + 1}年3月）の活動記録です。`}
        actions={
          <label className="flex items-center gap-2">
            <span className="text-wptext-muted">年度</span>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="rounded-sm border border-wpborder px-3 py-2"
            >
              {fiscalYearOptions().map((y) => (
                <option key={y} value={y}>
                  {y}年度
                </option>
              ))}
            </select>
          </label>
        }
      />

      {!isSelf && memberId && (
        <MemberRelatedNav memberId={memberId} current="portfolio" />
      )}

      <div className="flex flex-col gap-4">
        <ProfileBox data={data} />
        <ScoreBox data={data} />
        <EventBox data={data} />
        <SurveyBox data={data} />
        <PresidentBox data={data} />
      </div>
    </>
  );
}

function ProfileBox({ data }: { data: Portfolio }): ReactElement {
  const m = data.member;
  return (
    <MetaBox title="基本情報">
      <div className="flex flex-wrap items-start gap-4">
        <PhotoPreview photoUrl={m.photo_url} name={m.name} size={96} />
        <dl className="grid flex-1 grid-cols-[auto_1fr] gap-x-4 gap-y-1">
          <dt className="text-wptext-muted">会員ID</dt>
          <dd className="tabular-nums">{m.member_id}</dd>
          <dt className="text-wptext-muted">氏名</dt>
          <dd className="font-semibold text-wptext-strong">
            {m.name}
            {m.name_kana && <span className="ml-2 font-normal text-wptext-muted">{m.name_kana}</span>}
          </dd>
          <dt className="text-wptext-muted">会社・役職</dt>
          <dd>
            {m.company ?? '—'}
            {m.position && ` / ${m.position}`}
          </dd>
          <dt className="text-wptext-muted">管掌領域</dt>
          <dd>{m.specialty ?? '—'}</dd>
          <dt className="text-wptext-muted">期・コース・状態</dt>
          <dd>
            {m.cohort_name ?? '—'} ／ {m.course_name ?? '—'} ／{' '}
            {MEMBER_STATUS_LABELS[m.status as MemberStatus] ?? m.status}
          </dd>
          {m.bio && (
            <>
              <dt className="text-wptext-muted">自己紹介</dt>
              <dd className="whitespace-pre-wrap">{m.bio}</dd>
            </>
          )}
        </dl>
      </div>
    </MetaBox>
  );
}

function ScoreBox({ data }: { data: Portfolio }): ReactElement {
  return (
    <MetaBox title="採点・順位の推移">
      {data.scores.length === 0 ? (
        <p className="text-wptext-muted">この年度に公開された採点結果はまだありません。</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-wpborder text-wptext-muted">
                <th className="py-1 pr-4 font-normal">対象月</th>
                <th className="py-1 pr-4 font-normal">順位</th>
                <th className="py-1 pr-4 font-normal">総合点</th>
                <th className="py-1 font-normal">内訳</th>
              </tr>
            </thead>
            <tbody>
              {data.scores.map((s) => (
                <tr key={s.period} className="border-b border-wpborder last:border-0">
                  <td className="py-1 pr-4 tabular-nums">{s.period}</td>
                  <td className="py-1 pr-4 tabular-nums">{s.rank}位</td>
                  <td className="py-1 pr-4 tabular-nums">{s.score}</td>
                  <td className="py-1 text-wptext-muted">
                    {Object.keys(s.components).length === 0
                      ? '—'
                      : Object.entries(s.components)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(' / ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </MetaBox>
  );
}

function EventBox({ data }: { data: Portfolio }): ReactElement {
  const rate = data.attendance_rate;
  return (
    <MetaBox title="イベントの参加状況">
      {rate !== null && (
        <p className="mb-2">
          <span className="text-wptext-muted">出席率</span>{' '}
          <strong className="text-lg tabular-nums text-wptext-strong">
            {Math.round(rate * 100)}%
          </strong>
          <span className="ml-2 text-wptext-muted">
            （対象 {data.events.length}回のうち{' '}
            {data.events.filter((e) => e.attendance_status === 'onsite' || e.attendance_status === 'online').length}
            回に出席）
          </span>
        </p>
      )}
      {data.events.length === 0 ? (
        <p className="text-wptext-muted">この年度に対象となったイベントはありません。</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {data.events.map((e) => (
            <li key={e.event_id} className="flex flex-wrap items-baseline gap-x-2">
              <span className="whitespace-nowrap tabular-nums text-wptext-muted">
                {new Date(e.starts_at).toLocaleDateString('ja-JP', {
                  month: 'numeric',
                  day: 'numeric',
                })}
              </span>
              <span className="rounded-sm bg-wpcanvas px-2 py-0.5 text-xs">{e.type_name}</span>
              <Link to={`/events/${e.event_id}`} className="text-wpaccent hover:underline">
                {e.title}
              </Link>
              <span className="text-wptext-muted">
                {e.attendance_status
                  ? (ATTENDANCE_STATUS_LABELS[e.attendance_status as AttendanceStatus] ??
                    e.attendance_status)
                  : '—'}
              </span>
              {e.table_no !== null && (
                <span className="text-wptext-muted">卓 {e.table_no}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </MetaBox>
  );
}

/** 課題・アンケートの回答を月ごとにまとめて時系列で並べる（先方シートの月別構成に対応） */
function SurveyBox({ data }: { data: Portfolio }): ReactElement {
  if (data.surveys.length === 0) {
    return (
      <MetaBox title="課題・アンケートの記録">
        <EmptyState title="この年度に割り当てられた課題・アンケートはありません" />
      </MetaBox>
    );
  }

  const byMonth = new Map<string, SurveyEntry[]>();
  for (const survey of data.surveys) {
    const date = new Date(survey.occurred_at);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const list = byMonth.get(key) ?? [];
    list.push(survey);
    byMonth.set(key, list);
  }

  return (
    <MetaBox title="課題・アンケートの記録">
      <p className="mb-3 text-wptext-muted">
        提出した課題・アンケートの回答を、開催月ごとに並べています。
      </p>
      <div className="flex flex-col gap-4">
        {[...byMonth.entries()].map(([month, surveys]) => (
          <section key={month}>
            <h3 className="mb-1 border-b border-wpborder pb-1 font-semibold text-wptext-strong">
              {month.replace('-', '年')}月
            </h3>
            <div className="flex flex-col gap-3 pt-2">
              {surveys.map((s) => (
                <article key={s.event_survey_id}>
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="rounded-sm bg-wpcanvas px-2 py-0.5 text-xs">
                      {s.event_type_name}
                    </span>
                    <Link
                      to={`/events/${s.event_id}?tab=surveys`}
                      className="font-semibold text-wpaccent hover:underline"
                    >
                      {s.survey_name}
                    </Link>
                    <span
                      className={
                        s.status === 'submitted'
                          ? 'text-wptext-muted'
                          : 'font-semibold text-wpnotice-error'
                      }
                    >
                      {SURVEY_STATUS_LABELS[s.status] ?? s.status}
                    </span>
                    {s.submitted_at && (
                      <span className="text-wptext-muted">
                        {new Date(s.submitted_at).toLocaleDateString('ja-JP')} 提出
                      </span>
                    )}
                  </div>
                  {s.answers.length > 0 && (
                    <dl className="mt-1 flex flex-col gap-2 border-l-2 border-wpborder pl-3">
                      {s.answers.map((a) => (
                        <div key={a.question_id}>
                          <dt className="text-wptext-muted">{a.label}</dt>
                          <dd className="whitespace-pre-wrap text-wptext-strong">
                            {a.value || '—'}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>
    </MetaBox>
  );
}

function PresidentBox({ data }: { data: Portfolio }): ReactElement {
  return (
    <MetaBox title="社長からの期待・成長評価">
      {data.president.length === 0 ? (
        <p className="text-wptext-muted">
          表示できる回答はありません（開示範囲の設定により見えない場合があります）。
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {data.president.map((p, index) => (
            <article key={`${p.survey_type}-${index}`}>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-semibold text-wptext-strong">
                  {p.survey_type === 'start_expectation' ? '入口の期待' : '成長評価'}
                </span>
                {p.round_name && <span className="text-wptext-muted">{p.round_name}</span>}
                {p.answered_at && (
                  <span className="text-wptext-muted">
                    {new Date(p.answered_at).toLocaleDateString('ja-JP')} 聴取
                  </span>
                )}
              </div>
              <dl className="mt-1 flex flex-col gap-2 border-l-2 border-wpborder pl-3">
                {p.answers.map((a) => (
                  <div key={a.question_id}>
                    <dt className="text-wptext-muted">{a.label}</dt>
                    <dd className="whitespace-pre-wrap text-wptext-strong">{a.value || '—'}</dd>
                  </div>
                ))}
              </dl>
            </article>
          ))}
        </div>
      )}
    </MetaBox>
  );
}
